<template lang="pug">
.lh-large-article-card__wrapper(
  @mouseover="onMouseOver"
  ref="articleCard"
)
  TheLinkUi.lh-large-article-card__link(
    data-jest="link"
    @click="onClickLink"
    @contextmenu="onContextMenu"
    v-bind="{href: relativeLink, name: title, target: isTeaser ? '_blank' : ''}"
  )
  .lh-large-article-card(
    data-jest="card"
  )
    .lh-large-article-card__cover
      BasePicture(
      :usedSized="[390, 640, 718, 1170, 1500, 1945]"
      :sizesByDevice="{ mobile: '100vw', tablet: '718px', bigTablet: '782px' }"
      :loading="imgLoading"
      :alt="imgAlt || title"
      :sizes="sizes"
      :origin="getSourceImage(sizes)"
    )
    .lh-large-article-card__title(
      data-jest="title"
      v-html="title"
    )
    .lh-large-article-card__controls-and-meta
      ArticleCardControls.lh-large-article-card__controls(
        v-bind="getArticleCardControlsProps($props)"
        :style="cardControlsCssVariables"
        :favorite-icon-css-variables="cardControlsFavoriteIconCssVariables"
        @clicked-on-comments="onClickedComments"
        @favorite-delete="onFavoriteDeleteEmit"
        @favorite-create-folder-and-add="onFavoriteCreateFolderAndAddEmit"
        @favorite-create-folder-and-transfer="onFavoriteCreateFolderAndTransferEmit"
        @favorite-transfer-to-folder="onFavoriteTransferToFolderEmit"
        @favorite-add-to-folder="onFavoriteAddToFolderEmit"
        @favorite-deferred="onFavoriteDeferredEmit"
        @favorite-action="onFavoriteAction"
      )
      ArticleCardMeta.lh-large-article-card__meta(
        :dateText="dateText"
        :dateMoscow="dateMoscow"
        :isPromo="isPromo"
        :isAdvertising="isAdvertising"
        :categories="categories"
        :partnerText="partnerText"
        color="rgba(255,255,255, 0.8)"
        hoverColor="white"
        @clicked-on-author="onClickedOnAuthor"
        @clicked-on-category="onClickedOnCategory"
      )
    AdminControlsPanel.lh-large-article-card__admin-panel(
      v-if="isAuthenticatedInWordpress"
      :viewsCount="viewsCount"
      :editLinkHref="editLinkHref"
      :loading="loadingViews"
      @click-on-restore-button="onClickRestoreButton"
    )
  ArticleCardCategoryIcon.lh-large-article-card__category-icon(
    v-if="showCategoryIcon && categoryIcon"
    :size="40"
    :icon="categoryIcon"
    :href="categoryLink"
  )
</template>

<script lang="ts">
  import type { PropType } from 'vue';

  import type { Img } from '@devhacker/shared/utils/image/types';
  import type { User } from '@devhacker/shared/features/user/types/user';
  import TheLinkUi from '@/components/lh-ui/TheLinkUi/TheLink.vue';
  import ArticleCardControls from '@/components/lh-ui/ArticleCardControls/ArticleCardControls.vue';
  import AdminControlsPanel from '@/components/lh-ui/AdminControlsPanel/AdminControlsPanel.vue';
  import ArticleCardCategoryIcon from '@/components/lh-ui/ArticleCardCategoryIcon/ArticleCardCategoryIcon.vue';
  import ArticleCardMeta from '@/components/lh-ui/ArticleCardMeta/ArticleCardMeta.vue';
  import BasePicture from '@devhacker/shared/features/BasePicture/BasePicture.vue';
  import { getArticleCardControlsProps, onFavoriteAction } from '@/utils/articleCard';
  import { removeLifehackerFromUrl } from '~/utils/removeLifehackerFromUrl';
  import { getSourceImage } from '~/utils';
  import { useArticleCardCoverPreloadHead } from '~/domains/head/technical/services/useArticleCardCoverPreloadHead';

  export default defineNuxtComponent({
    name: 'ArticleCardLarge',
    components: {
      TheLinkUi,
      ArticleCardControls,
      AdminControlsPanel,
      ArticleCardCategoryIcon,
      ArticleCardMeta,
      BasePicture,
    },

    props: {
      url: {
        type: String as PropType<string>,
        required: true,
      },
      internalUrl: {
        type: Boolean as PropType<boolean>,
        required: false,
        default: false,
      },
      title: {
        type: String as PropType<string>,
        required: true,
      },
      imgAlt: {
        type: String as PropType<string>,
        required: false,
        default: '',
      },
      imgLoading: {
        type: String as PropType<'eager' | 'auto' | 'lazy'>,
        default: 'lazy',
      },
      images: {
        type: Array as PropType<Img[]>,
        required: false,
        default: null,
      },
      defaultImages: {
        type: Array as PropType<Img[]>,
        required: false,
        default: null,
      },
      dateText: {
        type: String,
        required: true,
      },
      dateMoscow: {
        type: String,
        required: true,
      },
      categories: {
        type: Array as PropType<Array<IArticleCategory>>,
        required: false,
        default: () => [],
      },
      commentsEnabled: {
        type: Boolean as PropType<boolean>,
        required: true,
      },
      commentsCount: {
        type: Number as PropType<number>,
        required: false,
        default: 0,
      },
      authorText: {
        type: String as PropType<string>,
        required: false,
        default: null,
      },
      authorUrl: {
        type: String as PropType<string>,
        required: false,
        default: null,
      },
      isAdvertising: {
        type: Boolean as PropType<boolean>,
        required: false,
        default: false,
      },
      isPromo: {
        type: Boolean as PropType<boolean>,
        required: false,
        default: false,
      },
      isTeaser: {
        type: Boolean as PropType<boolean>,
        required: false,
        default: false,
      },
      isAuthenticatedInWordpress: {
        type: Boolean as PropType<boolean>,
        required: false,
        default: false,
      },
      editLinkHref: {
        type: String as PropType<string>,
        required: false,
        default: null,
      },
      viewsCount: {
        type: Number as PropType<number>,
        required: false,
        default: null,
      },
      user: {
        type: Object as PropType<User>,
        default: null,
      },
      favorite: {
        required: false,
        type: Object as PropType<IFavoriteItem | null>,
        default: null,
      },
      folders: {
        required: true,
        type: Object as PropType<IFolders>,
      },
      loadingViews: {
        type: Boolean as PropType<boolean>,
        required: true,
      },
      advertLabel: {
        default: '',
        type: String as PropType<'empty' | 'partner' | 'abs' | 'press-release' | 'social-ads'>,
      },
      showCategoryIcon: {
        type: Boolean,
        default: true,
      },
      partnerText: {
        type: String as PropType<string>,
        default: '',
      },
      type: {
        type: String as PropType<string>,
        default: '',
      },
      adsToken: {
        default: '',
        type: String as PropType<string>,
      },
      tags: {
        type: Array as PropType<Array<IArticleTags>>,
        required: false,
        default: () => [],
      },
    },

    emits: [
      'click-on-article-card',
      'clicked-on-comments',
      'clicked-on-category',
      'clicked-on-author',
      'favorite-deferred',
      'favorite-add-to-folder',
      'favorite-transfer-to-folder',
      'favorite-create-folder-and-transfer',
      'favorite-create-folder-and-add',
      'favorite-delete',
      'click-on-restore-button',
      'show-context-menu',
    ],
    setup(props) {
      useArticleCardCoverPreloadHead({
        enabled: computed(() => props.imgLoading === 'eager'),
        images: computed(() =>
          props.images?.length
            ? props.images
            : props.defaultImages?.length
              ? props.defaultImages
              : [],
        ),
        sizes: [390, 640, 718, 1170, 1500, 1945],
        sizesByDevice: { mobile: '100vw', tablet: '718px', desktop: '782px' },
      });
    },
    computed: {
      categoryIcon(): string {
        const { categories } = this;
        const categoryWithIcon = categories.find((item) => Boolean(item?.icon));
        return categoryWithIcon?.icon ?? '';
      },
      categoryLink(): string | null {
        const { categories } = this;
        const categoryWithIcon = categories.find((item) => Boolean(item?.url));
        return categoryWithIcon?.url ?? null;
      },
      relativeLink() {
        return removeLifehackerFromUrl(this.url);
      },
      cardControlsCssVariables() {
        return {
          '--card-controls-color': 'rgba(255,255,255, 0.8)',
          '--card-controls-hover-color': 'white',
          '--card-controls-text-color': 'rgba(255,255,255, 0.8)',
          '--card-controls-text-hover-color': 'white',
        };
      },
      cardControlsFavoriteIconCssVariables() {
        return {
          '--favorite-icon-color': 'rgba(255,255,255, 0.8)',
          '--favorite-icon-hover-color': 'white',
          '--favorite-icon-active-color': 'var(--main-color)',
        };
      },
      sizes() {
        return this.images?.length
          ? this.images
          : this.defaultImages?.length
            ? this.defaultImages
            : [];
      },
    },
    methods: {
      getSourceImage,
      getArticleCardControlsProps,
      onFavoriteAction,
      onMouseOver(): void {
        const articleCard = this.$refs.articleCard as HTMLElement;
        if (articleCard) {
          articleCard.classList.remove('lh-article-card--no-hover');
        }
      },
      onClickedComments(link: string) {
        this.$emit('clicked-on-comments', link);
      },
      onClickedOnCategory(link: string, categoryName: string) {
        this.$emit('clicked-on-category', link, categoryName, this.title || '');
      },
      onClickedOnAuthor(link: string): void {
        this.$emit('clicked-on-author', link);
      },
      onClickLink() {
        this.$emit('click-on-article-card');
      },
      onContextMenu() {
        this.$emit('show-context-menu');
      },
      onFavoriteDeferredEmit(deferredAction: Function, ctx: any) {
        this.$emit('favorite-deferred', deferredAction, ctx);
      },
      onFavoriteAddToFolderEmit(folder: IFolderItem) {
        this.$emit('favorite-add-to-folder', folder);
      },
      onFavoriteTransferToFolderEmit(favorite: IFavoriteItem, folder: IFolderItem) {
        this.$emit('favorite-transfer-to-folder', favorite, folder);
      },
      onFavoriteCreateFolderAndTransferEmit(nameFolder: string, favorite: IFavoriteItem): void {
        this.$emit('favorite-create-folder-and-transfer', nameFolder, favorite);
      },
      onFavoriteCreateFolderAndAddEmit(nameFolder: string): void {
        this.$emit('favorite-create-folder-and-add', nameFolder);
      },
      onFavoriteDeleteEmit(favorite: IFavoriteItem): void {
        this.$emit('favorite-delete', favorite);
      },
      onClickRestoreButton() {
        this.$emit('click-on-restore-button');
      },
    },
  });
</script>

<style lang="scss" src="./ArticleCardLarge.scss"></style>
